/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import FloatingInput from "../../../components/input/FloatingInput";
import CustomSelect from "../../../components/customselect/CustomSelect";
import { Controller } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import api from "../../../api/axios";
import { ThemeContext } from "../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import { validateCounty } from "../buyer/services/buyerNetService";
import { setBuyerFormData } from "../../../store/slices/buyerSlice";

const normId = (id) =>
  id === null || id === undefined || id === "" ? "" : String(id);

const normStr = (value) => String(value ?? "").trim().toLowerCase();

const getStateCode = (state) =>
  normStr(
    state?.statecode ||
      state?.state_code ||
      state?.code ||
      state?.abbreviation ||
      state?.state ||
      "",
  );

const getAddressMetaFromForm = (getValues, fallback = {}) => {
  if (typeof getValues !== "function") return fallback || {};
  try {
    return getValues("addressMeta") || fallback || {};
  } catch (e) {
    return fallback || {};
  }
};

const getZipFromMeta = (meta = {}) =>
  String(meta.closing_zip_code || meta.zip || "").trim();

const findStateMatch = (statesList = [], meta = {}) => {
  const list = statesList.filter((s) => s && s.id !== "" && s.id != null);
  if (!list.length) return null;

  if (meta.stateid) {
    const byId = list.find((s) => normId(s.id) === normId(meta.stateid));
    if (byId) return byId;
  }

  const closingState = normStr(meta.closing_state);
  const stateCode = normStr(meta.state);

  if (closingState) {
    const byName = list.find((s) => normStr(s.statename) === closingState);
    if (byName) return byName;
    const byCode = list.find((s) => getStateCode(s) === closingState);
    if (byCode) return byCode;
  }

  if (stateCode) {
    const byCode = list.find((s) => getStateCode(s) === stateCode);
    if (byCode) return byCode;
    const byName = list.find((s) => normStr(s.statename) === stateCode);
    if (byName) return byName;
  }

  return null;
};

const findCountyMatch = (countiesList = [], meta = {}) => {
  const list = countiesList.filter((c) => c && c.id !== "" && c.id != null);
  if (!list.length) return null;

  if (meta.countyid) {
    const byId = list.find((c) => normId(c.id) === normId(meta.countyid));
    if (byId) return byId;
  }

  const countyName = normStr(meta.closing_county || meta.county);
  if (!countyName) return null;

  return (
    list.find(
      (c) => normStr(c.countyname || c.name || c.county) === countyName,
    ) || null
  );
};

const buildClosingCountyState = (meta = {}) => ({
  stateid: meta.stateid || "",
  countyid: meta.countyid || "",
  state: meta.closing_state || meta.state || "",
  county: meta.closing_county || meta.county || "",
  zipcode: getZipFromMeta(meta),
});

export default function SetCountyModal({ control, onClose, setValue, calc, getValues }) {
  // Always call hooks at top-level
  const dispatch = useDispatch();
  const buyerFormData = useSelector((state) => state.buyer.formData || {});

  const theme = useContext(ThemeContext);
  const { t: trans } = useTranslation();

  // Derive a sanitized `formData` that the rest of the component can use safely.
  // If calc === 'seller', prefer the provided getValues() (form scope in parent)
  // otherwise use the buyer data from Redux.
  const sellerAddressMeta = getAddressMetaFromForm(getValues);
  const formData = calc === "seller" ? { addressMeta: sellerAddressMeta } : buyerFormData;
  const currentAddressMeta =
    calc === "seller"
      ? sellerAddressMeta
      : buyerFormData?.addressMeta || getAddressMetaFromForm(getValues);

  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);

  const [closingCountydata, setClosingCountydata] = useState(() =>
    buildClosingCountyState(currentAddressMeta),
  );

  const [error, setError] = useState({ isError: false, errorMessage: "" });

  const applyStateSelection = (statesList) => {
    const meta =
      calc === "seller"
        ? getAddressMetaFromForm(getValues, formData?.addressMeta || {})
        : buyerFormData?.addressMeta ||
          getAddressMetaFromForm(getValues, formData?.addressMeta || {});
    const found = findStateMatch(statesList, meta);
    if (!found) return;

    setClosingCountydata((prev) => ({
      ...prev,
      stateid: found.id,
      state: found.statename,
      zipcode: getZipFromMeta(meta) || prev.zipcode,
    }));
  };

  const applyCountySelection = (countiesList) => {
    const meta =
      calc === "seller"
        ? getAddressMetaFromForm(getValues, formData?.addressMeta || {})
        : buyerFormData?.addressMeta ||
          getAddressMetaFromForm(getValues, formData?.addressMeta || {});
    const found = findCountyMatch(countiesList, meta);
    if (!found) return;

    setClosingCountydata((prev) => ({
      ...prev,
      countyid: found.id,
      county: found.countyname || found.name || found.county || "",
    }));
  };

  // Seed local modal state from the current form when opened.
  useEffect(() => {
    const meta =
      calc === "seller"
        ? getAddressMetaFromForm(getValues, formData?.addressMeta || {})
        : buyerFormData?.addressMeta ||
          getAddressMetaFromForm(getValues, formData?.addressMeta || {});
    setClosingCountydata(buildClosingCountyState(meta));

    if (calc === "seller" && typeof setValue === "function") {
      const zip = getZipFromMeta(meta);
      if (zip && !meta.closing_zip_code) {
        try {
          setValue("addressMeta.closing_zip_code", zip, {
            shouldDirty: false,
            shouldTouch: false,
          });
        } catch (e) {
          /* ignore */
        }
      }
    }
  }, [calc, getValues, setValue, buyerFormData?.addressMeta, formData?.addressMeta]);

  // ✅ Fetch states (with per-company cache)
  useEffect(() => {
    if (!theme?.id) return; // wait for theme to load

    const mainCompanyId = theme.id;

    const CACHE_KEY = `states_${mainCompanyId}`;
    const CACHE_TIME_KEY = `${CACHE_KEY}_time`;
    const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
    const now = Date.now();

    const cachedStates = localStorage.getItem(CACHE_KEY);
    const cachedTime = localStorage.getItem(CACHE_TIME_KEY);

    // Use cached if fresh
    if (cachedStates && cachedTime && now - parseInt(cachedTime, 10) < CACHE_DURATION) {
      try {
        const parsedStates = JSON.parse(cachedStates);
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...parsedStates]);
        applyStateSelection(parsedStates);
        return;
      } catch (err) {
        console.warn("Invalid cached states, refetching...", err);
      }
    }

    // Fetch states
    api
      .get(`/auth/states`, { params: { maincompanyid: mainCompanyId } })
      .then((res) => {
        const fetchedStates = res.data.data || [];
        const selectOption = { id: "", statename: trans("register_state") };
        setStates([selectOption, ...fetchedStates]);

        // Cache
        localStorage.setItem(CACHE_KEY, JSON.stringify(fetchedStates));
        localStorage.setItem(CACHE_TIME_KEY, now.toString());
        applyStateSelection(fetchedStates);
      })
      .catch((err) => console.error("Failed to fetch states:", err));
  }, [
    theme?.id,
    theme?.maincompanyid,
    trans,
    formData?.addressMeta?.closing_state,
    formData?.addressMeta?.state,
    formData?.addressMeta?.stateid,
    getValues,
  ]);

  // ✅ Fetch counties when a state is chosen
  useEffect(() => {
    setCounties([]);
    if (!closingCountydata.stateid) return;

    const meta =
      calc === "seller"
        ? getAddressMetaFromForm(getValues, formData?.addressMeta || {})
        : buyerFormData?.addressMeta ||
          getAddressMetaFromForm(getValues, formData?.addressMeta || {});
    setClosingCountydata((prev) => ({
      ...prev,
      county: meta.closing_county || meta.county || prev.county || "",
      countyid: meta.countyid || prev.countyid || "",
      zipcode: getZipFromMeta(meta) || prev.zipcode,
    }));

    api
      .get(`/auth/counties`, {
        params: {
          maincompanyid: theme.id,
          stateId: closingCountydata?.stateid,
        },
      })
      .then((res) => {
        const fetchedCounties = res.data.data || [];
        const selectOption = { id: "", countyname: trans("register_county") };
        setCounties([selectOption, ...fetchedCounties]);
        applyCountySelection(fetchedCounties);
      })
      .catch((err) => console.error("Failed to fetch counties:", err));
  }, [
    closingCountydata.stateid,
    theme?.id,
    trans,
    formData?.addressMeta?.closing_county,
    formData?.addressMeta?.county,
    formData?.addressMeta?.countyid,
    getValues,
  ]);

  // Validate county data
  const handleValidateCountyData = async () => {
    const countyData = { ...closingCountydata };
    setError({ isError: false, errorMessage: "" });

    if (!countyData.stateid || !countyData.countyid) {
      setError({ isError: true, errorMessage: "Please select state and county" });
      return;
    }

    const zipCodeRegex = /^[0-9]{5}$/;
    if (countyData.zipcode && !zipCodeRegex.test(countyData.zipcode)) {
      setError({ isError: true, errorMessage: "Please enter valid zip code" });
      return;
    }

    try {
      const getValidatedData = await validateCounty(countyData);
      if (getValidatedData) {
        // If parent passed setValue (react-hook-form), update form values
        if (typeof setValue === "function") {
          setValue("addressMeta.closing_state", getValidatedData?.closing_state, {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("addressMeta.closing_county", getValidatedData?.closing_county, {
            shouldDirty: true,
            shouldTouch: true,
          });
          setValue("addressMeta.closing_zip_code", getValidatedData?.closing_zip_code, {
            shouldDirty: true,
            shouldTouch: true,
          });
          if (calc === "seller" && getValidatedData?.closing_zip_code) {
            setValue("addressMeta.zip", getValidatedData.closing_zip_code, {
              shouldDirty: true,
              shouldTouch: true,
            });
          }
        }

        // If this is buyer flow, update redux buyer form data
        if (calc === "buyer") {
          dispatch(
            setBuyerFormData({
              ...buyerFormData,
              addressMeta: {
                ...(buyerFormData?.addressMeta || {}),
                closing_state: getValidatedData?.closing_state,
                closing_county: getValidatedData?.closing_county,
                closing_zip_code: getValidatedData?.closing_zip_code,
              },
            })
          );
        } else if (calc === "seller") {
          // Merge with existing addressMeta to avoid wiping other fields (unitNumber, addressIncludesUnit, etc.)
          try {
            const prev = typeof getValues === 'function' ? (getValues('addressMeta') || {}) : {};
            setValue('addressMeta', {
              ...(prev || {}),
              closing_state: getValidatedData?.closing_state,
              closing_county: getValidatedData?.closing_county,
              closing_zip_code: getValidatedData?.closing_zip_code,
              zip: getValidatedData?.closing_zip_code || prev?.zip || '',
              stateid: closingCountydata.stateid || prev?.stateid || '',
              countyid: closingCountydata.countyid || prev?.countyid || '',
            }, { shouldDirty: true, shouldTouch: true });
          } catch (e) {
            // fallback to non-merged setValue if getValues not available
            try {
              setValue('addressMeta', {
                closing_state: getValidatedData?.closing_state,
                closing_county: getValidatedData?.closing_county,
                closing_zip_code: getValidatedData?.closing_zip_code,
              });
            } catch (err) {
              console.warn('Failed merging addressMeta in SetCountyModal', err);
            }
          }
        }

        onClose();
        resetState();
      }
    } catch (err) {
      console.error("validateCounty failed:", err);
      setError({ isError: true, errorMessage: "Failed to validate county. Please try again." });
    }
  };

  const resetState = () => {
    setClosingCountydata({
      stateid: "",
      countyid: "",
      state: "",
      county: "",
      zipcode: "",
    });
  };

  return (
    <div className="modelspace pplication-fe model-select">
      <p className="m-b-20 pop-desc heading-b">
        Please select the county for this property. The county affects local tax
        rates and fees.
      </p>

      <div className="frm-field-1 model-select-lbl">
        <div className="form-group">
          <Controller
            name="addressMeta.closing_state"
            control={control}
            defaultValue={formData?.addressMeta?.closing_state ?? ""}
            render={({ field }) => (
              <CustomSelect
                options={states.map(s => ({ value: s.id, label: s.statename }))} // normalize format
                value={closingCountydata.stateid}   // ← use id
                onChange={(value, option) => {
                  setClosingCountydata(prev => ({
                    ...prev,
                    stateid: value,
                    state: option.label,
                  }));
                  field.onChange(option.label);  // store readable label in form
                }}
              />

            )}
          />
          <label> State <span>*</span>
          </label>
        </div>
      </div>

      <div className="frm-field-1 model-select-lbl">
        <div className="form-group">
          <Controller
            name="addressMeta.closing_county"
            control={control}
            defaultValue={formData?.addressMeta?.closing_county?.toUpperCase() ?? ""}
            render={({ field }) => (
              <CustomSelect
                options={counties.map(c => ({ value: c.id, label: c.countyname }))}
                value={closingCountydata.countyid}
                onChange={(value, option) => {
                  setClosingCountydata(prev => ({
                    ...prev,
                    countyid: value,
                    county: option.label,
                  }));
                  field.onChange(option.label);
                }}
              />

            )}
          />
          <label> County <span>*</span>
          </label>
        </div>
      </div>

      <div className="frm-field-1 model-select-lbl">
        <div className="form-group">
          <Controller
            name="addressMeta.closing_zip_code"
            control={control}
            defaultValue={getZipFromMeta(currentAddressMeta)}
            render={({ field }) => (
              <FloatingInput
                {...field}
                value={field.value || closingCountydata.zipcode || ""}
                placeholder=""
                type="text"
                onChange={(e) => {
                  setClosingCountydata((prev) => ({
                    ...prev,
                    zipcode: e.target.value,
                  }));
                  return field.onChange(e.target.value);
                }}
              />
            )}
          />
          <label> Zip Code</label>
        </div>
      </div>

      {error.isError && (
        <p className="m-b-20" style={{ fontSize: "14px", color: "red" }}>
          {error.errorMessage}
        </p>
      )}

      <p className="m-b-20 pop-desc heading-b">
        If zip code is left blank, the system assigns a default zip code for the
        selected state and county.
      </p>

      <div className="frm-btn popup-2-btn">
        <button className="submit-btn btn-bg inpt-btn btn-discard" onClick={onClose}>
          Cancel
        </button>
        <button className="submit-btn btn-bg inpt-btn" onClick={handleValidateCountyData}>
          Continue
        </button>
      </div>
    </div>
  );
}
